import React, { useEffect, useRef } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { find, includes, propEq, propOr } from "ramda";
import { Link as RouterLink } from "react-router-dom";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import TablePagination from "@mui/material/TablePagination";

import EditIcon from "@mui/icons-material/Edit";
import { notBlank } from "../util/ext";
import { useMediaTable } from "../creator-application/common/use-mediaTable";
import Tooltip from "@mui/material/Tooltip";
import { Box, Link } from "@mui/material";
import AuthenticationDetailsCell from "./AuthenticationDetailsCell";
import { authTypes } from "./authenticationService";
import ConfirmButton from "../components/ConfirmButton";
import { useOrganizations } from "../user-admin/userServices";
import { locality } from "../util/organizationUtil";

const useStyles = makeStyles(theme => ({
  root: {
    width: "calc(100% + 12px)",
    marginLeft: "-6px",
    borderRadius: "20px",
  },

  icon: {
    color: theme.palette.primary.brandPrimary,
  },

  cell: {
    minWidth: 100,
    maxWidth: 400,
  },

  link: {
    lineHeight: 1.43,
    width: 300,
    color: theme.palette.primary.brandPrimary,
    flexDirection: "column",
    alignItems: "flex-start",
  },

  typeCell: {
    textTransform: "capitalize",
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },

  pagination: {
    color: theme.palette.primary.darker,
    backgroundColor: "white",
    borderRadius: "20px",
  },
}));

const AuthenticationTable = props => {
  const {
    authenticationList,
    page,
    onPageChange,
    total = 0,
    onSelectChange,
    orderBy = "organizationName",
    order = "asc",
    onSortChange,
    onScrollVisible,
    onDeleteClicked,
    onEditClicked,
  } = props;

  const classes = useStyles();
  const {
    state,
    handleRequestSort,
    handleChangePage,
    handleRowClick,
  } = useMediaTable(
    {
      onSortChange,
      onPageChange,
      onSelectChange,
    },
    {
      sort: {
        property: orderBy,
        order,
      },
      page,
      selected: [],
      editId: null,
    },
  );

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    console.log("Setting scroll visible...");
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible]);

  const organizations = useOrganizations();
  const findLocality = id => {
    const org = find(propEq("organizationId", id), organizations.data || []);
    return notBlank(org) && notBlank(org.location) ? locality(org) : "-";
  };

  return (
    <Paper elevation={0} className={classes.root}>
      <TableContainer ref={tableContainerEl}>
        <Table stickyHeader aria-label="Authentications">
          <TableHead>
            <TableRow>
              <TableCell
                key="organization"
                align="left"
                className={classes.cell}
              >
                <TableSortLabel
                  active={orderBy === "organizationName"}
                  direction={order}
                  onClick={() => handleRequestSort("organizationName")}
                >
                  Organization
                  {orderBy === "organizationName" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell key="alias" align="left" className={classes.cell}>
                <TableSortLabel
                  active={orderBy === "organizationAlias"}
                  direction={order}
                  onClick={() => handleRequestSort("organizationAlias")}
                >
                  Alias
                  {orderBy === "organizationAlias" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell key="type" align="left" className={classes.cell}>
                <TableSortLabel
                  active={orderBy === "type"}
                  direction={order}
                  onClick={() => handleRequestSort("type")}
                >
                  Type
                  {orderBy === "type" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell
                key="date-updated"
                align="left"
                className={classes.cell}
              >
                <TableSortLabel
                  active={orderBy === "dateUpdated"}
                  direction={order}
                  onClick={() => handleRequestSort("dateUpdated")}
                >
                  Last Update
                  {orderBy === "dateUpdated" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell key="updated-by" align="left" className={classes.cell}>
                <TableSortLabel
                  active={orderBy === "updatedBy"}
                  direction={order}
                  onClick={() => handleRequestSort("type")}
                >
                  Updated By
                  {orderBy === "updatedBy" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell key="details" align="left" className={classes.cell}>
                Details
              </TableCell>

              <TableCell key="actions" align="center" style={{ width: 100 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {authenticationList.map(auth => (
              <TableRow
                onClick={() => handleRowClick(auth)}
                hover
                tabIndex={-1}
                key={auth.authenticationId}
                selected={includes(auth.authenticationId, state.selected)}
              >
                <TableCell
                  key={"organization-name:" + auth.authenticationId}
                  align="left"
                  className={classes.cell}
                >
                  <Link
                    classes={{ root: classes.link }}
                    component={RouterLink}
                    to={{
                      pathname: `/manage-organizations/organizations/${auth.organizationId}`,
                    }}
                    state={{
                      label: "Back to Manage Authentication",
                      previousLocation: "/manage-authentication",
                    }}
                    aria-label={`View ${auth.organizationName}`}
                  >
                    <Box fontWeight="700">{auth.organizationName}</Box>
                    <Box fontWeight="700">
                      {findLocality(auth.organizationId)}
                    </Box>
                  </Link>
                </TableCell>

                <TableCell
                  key={"organization-alias:" + auth.authenticationId}
                  align="left"
                  className={classes.cell}
                >
                  {auth.organizationAlias}
                </TableCell>

                <TableCell
                  key={"type:" + auth.authenticationId}
                  align="left"
                  className={classes.cell}
                >
                  {propOr(
                    auth.type,
                    "display",
                    find(propEq("value", auth.type), authTypes),
                  )}
                </TableCell>

                <TableCell
                  key={"date-updated:" + auth.authenticationId}
                  align="left"
                  className={classes.cell}
                >
                  {new Date(auth.dateUpdated).toLocaleDateString()}
                </TableCell>

                <TableCell
                  key={"updated-by:" + auth.authenticationId}
                  align="left"
                  className={classes.cell}
                >
                  {auth.updatedByUsername}
                </TableCell>

                <TableCell
                  key={"details:" + auth.authenticationId}
                  align="left"
                  className={classes.cell}
                >
                  <AuthenticationDetailsCell authentication={auth} />
                </TableCell>

                <TableCell
                  key={"actions:" + auth.authenticationId}
                  align="center"
                  style={{ width: 100 }}
                >
                  <Box display="flex" justifyContent="space-between">
                    {(auth.type === "sip" ||
                      auth.type === "papi" ||
                      auth.type === "barcode") && (
                      <Tooltip title="Edit">
                        <IconButton
                          classes={{ label: classes.icon }}
                          onClick={() => onEditClicked(auth)}
                          size="large"
                        >
                          <EditIcon fontSize="large" />
                        </IconButton>
                      </Tooltip>
                    )}

                    <ConfirmButton
                      dialogTitle="Delete Authentication"
                      dialogBody="Are you sure you want to delete this authentication?"
                      handleDelete={() => onDeleteClicked(auth)}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        classes={{
          root: classes.pagination,
        }}
        rowsPerPageOptions={[10]}
        component="div"
        count={total}
        rowsPerPage={10}
        page={page}
        onPageChange={(event, page) => handleChangePage(page)}
      />
    </Paper>
  );
};

export default AuthenticationTable;
